import React, { useState, useEffect } from "react";
import "../css/CollegeAccountUpdate.css";
import {
  CreditCard,
  Smartphone,
  Building,
  Upload,
  Save,
  AlertCircle,
  CheckCircle,
  Trash2,
} from "lucide-react";
import axios from "axios"; // Make sure axios is installed

function CollegeAccountUpdate() {
  // State variables
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [currentMethod, setCurrentMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  // Load payment methods from the database
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          "https://payroll-api.uniums.com/api/payment-methods"
        );
        if (response.data && response.data.methods) {
          // Transform the data to match our component state structure
          const formattedMethods = response.data.methods.map(
            (method, index) => ({
              id: index + 1,
              type: method.type,
              name: method.displayName,
              upiId: method.upiId || "",
              // In your React component, change this line
              qrCode: method.qrCode
                ? `https://payroll-api.uniums.com/uploads/qrcodes/${method.qrCode}`
                : null,
              accountName: method.accountName || "",
              accountNumber: method.accountNumber || "",
              ifscCode: method.ifscCode || "",
              bankName: method.bankName || "",
              branch: method.branch || "",
              isActive: true, // Assuming all methods are active by default
            })
          );
          setPaymentMethods(formattedMethods);
        } else {
          // Load default methods if no data found
          setPaymentMethods([
            {
              id: 1,
              type: "gpay",
              name: "Google Pay",
              upiId: "college.gpay@okaxis",
              qrCode: null,
              isActive: true,
            },
            {
              id: 2,
              type: "phonepe",
              name: "PhonePe",
              upiId: "college.phonepe@ybl",
              qrCode: null,
              isActive: true,
            },
            {
              id: 3,
              type: "bank",
              name: "Bank Transfer",
              accountName: "College Name",
              accountNumber: "123456789012",
              ifscCode: "BANK0001234",
              bankName: "National Bank",
              branch: "Main Branch",
              isActive: true,
            },
            {
              id: 4,
              type: "paytm",
              name: "Paytm",
              upiId: "college.paytm@paytm",
              qrCode: null,
              isActive: true,
            },
            {
              id: 5,
              type: "bank",
              name: "Additional Bank Account",
              accountName: "College Secondary Account",
              accountNumber: "9876543210",
              ifscCode: "BANK9876543",
              bankName: "State Bank",
              branch: "City Branch",
              isActive: true,
            },
          ]);
        }
      } catch (error) {
        console.error("Error fetching payment methods:", error);
        // Load default methods on error
        setPaymentMethods([
          {
            id: 1,
            type: "gpay",
            name: "Google Pay",
            upiId: "college.gpay@okaxis",
            qrCode: null,
            isActive: true,
          },
          {
            id: 2,
            type: "phonepe",
            name: "PhonePe",
            upiId: "college.phonepe@ybl",
            qrCode: null,
            isActive: true,
          },
          {
            id: 3,
            type: "bank",
            name: "Bank Transfer",
            accountName: "College Name",
            accountNumber: "123456789012",
            ifscCode: "BANK0001234",
            bankName: "National Bank",
            branch: "Main Branch",
            isActive: true,
          },
          {
            id: 4,
            type: "paytm",
            name: "Paytm",
            upiId: "college.paytm@paytm",
            qrCode: null,
            isActive: true,
          },
          {
            id: 5,
            type: "bank",
            name: "Additional Bank Account",
            accountName: "College Secondary Account",
            accountNumber: "9876543210",
            ifscCode: "BANK9876543",
            bankName: "State Bank",
            branch: "City Branch",
            isActive: true,
          },
        ]);
        setNotification({
          type: "error",
          message: "Failed to load payment methods. Using default values.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentMethods();
  }, []);

  // Function to handle selecting a payment method for editing
  const handleSelectMethod = (method) => {
    setCurrentMethod({ ...method });
  };

  // Handle file upload for QR code
  const handleQrCodeUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Only process image files
    if (!file.type.match("image.*")) {
      setNotification({
        type: "error",
        message: "Please select an image file for the QR code",
      });
      return;
    }

    // Create a preview URL
    const reader = new FileReader();
    reader.onload = (event) => {
      setCurrentMethod({
        ...currentMethod,
        qrCode: event.target.result,
        qrCodeFile: file, // Store the file for later upload
      });
    };
    reader.readAsDataURL(file);
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentMethod({
      ...currentMethod,
      [name]: value,
    });
  };

  // Toggle active status
  const handleToggleActive = () => {
    setCurrentMethod({
      ...currentMethod,
      isActive: !currentMethod.isActive,
    });
  };

  // Save changes
  const handleSaveChanges = async () => {
    setLoading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("type", currentMethod.type);
      formData.append("displayName", currentMethod.name);

      // Add method-specific fields
      if (["gpay", "phonepe", "paytm"].includes(currentMethod.type)) {
        formData.append("upiId", currentMethod.upiId);
        if (currentMethod.qrCodeFile) {
          formData.append("qrCode", currentMethod.qrCodeFile);
        }
      } else if (currentMethod.type === "bank") {
        formData.append("accountName", currentMethod.accountName);
        formData.append("accountNumber", currentMethod.accountNumber);
        formData.append("ifscCode", currentMethod.ifscCode);
        formData.append("bankName", currentMethod.bankName);
        formData.append("branch", currentMethod.branch || "");
      }

      // Send the data to the server
      const response = await axios.post(
        "https://payroll-api.uniums.com/api/payment-methods/save",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Update the local state with the saved data
      if (response.data.paymentMethod) {
        // Fetch updated payment methods
        const updatedResponse = await axios.get(
          "https://payroll-api.uniums.com/api/payment-methods"
        );
        if (updatedResponse.data && updatedResponse.data.methods) {
          const formattedMethods = updatedResponse.data.methods.map(
            (method, index) => ({
              id: index + 1,
              type: method.type,
              name: method.displayName,
              upiId: method.upiId || "",
              qrCode: method.qrCode
                ? `/uploads/qrcodes/${method.qrCode}`
                : null,
              accountName: method.accountName || "",
              accountNumber: method.accountNumber || "",
              ifscCode: method.ifscCode || "",
              bankName: method.bankName || "",
              branch: method.branch || "",
              isActive: true,
            })
          );
          setPaymentMethods(formattedMethods);
        }
      }

      // Show success notification
      setNotification({
        type: "success",
        message: `${currentMethod.name} details updated successfully!`,
      });

      // Clear notification after 3 seconds
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    } catch (error) {
      console.error("Error saving payment method:", error);
      setNotification({
        type: "error",
        message: "Failed to save payment method. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get icon based on payment method type
  const getMethodIcon = (type) => {
    switch (type) {
      case "gpay":
      case "phonepe":
      case "paytm":
        return <Smartphone size={24} />;
      case "bank":
        return <Building size={24} />;
      default:
        return <CreditCard size={24} />;
    }
  };

  // Get color based on payment method type
  const getMethodColor = (type) => {
    switch (type) {
      case "gpay":
        return "#1a73e8";
      case "phonepe":
        return "#5f259f";
      case "paytm":
        return "#00baf2";
      case "bank":
        return "#344054";
      default:
        return "#64748b";
    }
  };

  // Function to add a new payment method
  const handleAddNewMethod = (type) => {
    let newMethod = {
      id: paymentMethods.length + 1,
      type: type,
      isActive: true,
    };

    if (type === "bank") {
      newMethod = {
        ...newMethod,
        name: "New Bank Account",
        accountName: "",
        accountNumber: "",
        ifscCode: "",
        bankName: "",
        branch: "",
      };
    } else {
      newMethod = {
        ...newMethod,
        name:
          type === "gpay"
            ? "Google Pay"
            : type === "phonepe"
            ? "PhonePe"
            : "Paytm",
        upiId: "",
        qrCode: null,
      };
    }

    setPaymentMethods([...paymentMethods, newMethod]);
    setCurrentMethod(newMethod);
  };

  return (
    <div className="college-account-update-container">
      {/* Header */}
      <div className="account-update-header">
        <h1>College Payment Methods Configuration</h1>
        <p>Update your institution's payment collection methods and details</p>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.type === "success" ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="loading-container">
          <div className="spinner"></div>
          <span>Processing...</span>
        </div>
      )}

      <div className="payment-methods-dashboard">
        {/* Payment Methods List */}
        <div className="payment-methods-list">
          <h2>Available Methods</h2>

          <div className="methods-grid">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className={`method-card ${
                  currentMethod && currentMethod.id === method.id
                    ? "selected"
                    : ""
                } ${!method.isActive ? "inactive" : ""}`}
                onClick={() => handleSelectMethod(method)}
              >
                <div
                  className="method-icon"
                  style={{ backgroundColor: getMethodColor(method.type) }}
                >
                  {getMethodIcon(method.type)}
                </div>
                <div className="method-info">
                  <h3>{method.name}</h3>
                  {method.type === "bank" ? (
                    <p>{method.bankName}</p>
                  ) : (
                    <p>{method.upiId}</p>
                  )}
                  <span
                    className={`status-badge ${
                      method.isActive ? "active" : "inactive"
                    }`}
                  >
                    {method.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            ))}

            {/* Add new method cards */}
            <div className="add-method-section">
              <button
                className="add-method-button"
                onClick={() => handleAddNewMethod("bank")}
              >
                <Building size={20} />
                Add Bank Account
              </button>
              <button
                className="add-method-button"
                onClick={() => handleAddNewMethod("gpay")}
              >
                <Smartphone size={20} />
                Add Google Pay
              </button>

              <button
                className="add-method-button"
                onClick={() => handleAddNewMethod("phonepe")}
              >
                <Smartphone size={20} />
                Add PhonePe
              </button>
              <button
                className="add-method-button"
                onClick={() => handleAddNewMethod("paytm")}
              >
                <Smartphone size={20} />
                Add Paytm
              </button>
            </div>
          </div>
        </div>

        {/* Payment Method Editor */}
        {currentMethod && (
          <div className="payment-method-editor">
            <h2>Edit {currentMethod.name}</h2>

            <div className="editor-form">
              {/* Common fields */}
              <div className="form-group">
                <label className="form-label">Display Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={currentMethod.name}
                  onChange={handleInputChange}
                />
              </div>

              {/* UPI-specific fields */}
              {["gpay", "phonepe", "paytm"].includes(currentMethod.type) && (
                <div className="form-group">
                  <label className="form-label">UPI ID</label>
                  <input
                    type="text"
                    name="upiId"
                    className="form-input"
                    placeholder="yourname@upi"
                    value={currentMethod.upiId}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              {/* Bank-specific fields */}
              {currentMethod.type === "bank" && (
                <>
                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label className="form-label">Account Name</label>
                        <input
                          type="text"
                          name="accountName"
                          className="form-input"
                          value={currentMethod.accountName}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label className="form-label">Account Number</label>
                        <input
                          type="text"
                          name="accountNumber"
                          className="form-input"
                          value={currentMethod.accountNumber}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-col">
                      <div className="form-group">
                        <label className="form-label">IFSC Code</label>
                        <input
                          type="text"
                          name="ifscCode"
                          className="form-input"
                          value={currentMethod.ifscCode}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="form-col">
                      <div className="form-group">
                        <label className="form-label">Bank Name</label>
                        <input
                          type="text"
                          name="bankName"
                          className="form-input"
                          value={currentMethod.bankName}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Branch</label>
                    <input
                      type="text"
                      name="branch"
                      className="form-input"
                      value={currentMethod.branch}
                      onChange={handleInputChange}
                    />
                  </div>
                </>
              )}

              {/* QR Code upload (for UPI methods) */}
              {["gpay", "phonepe", "paytm"].includes(currentMethod.type) && (
                <div className="form-group">
                  <label className="form-label">QR Code</label>
                  <div className="qr-upload-container">
                    {currentMethod.qrCode ? (
                      <div className="qr-preview">
                        <img src={currentMethod.qrCode} alt="QR Code" />
                        <button
                          type="button"
                          className="remove-qr-button"
                          onClick={() =>
                            setCurrentMethod({
                              ...currentMethod,
                              qrCode: null,
                              qrCodeFile: null,
                            })
                          }
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="qr-upload-box">
                        <input
                          type="file"
                          id="qrCodeUpload"
                          accept="image/*"
                          className="file-input"
                          onChange={handleQrCodeUpload}
                        />
                        <label htmlFor="qrCodeUpload" className="upload-label">
                          <Upload size={24} />
                          <span>Click to upload QR code</span>
                          <span className="upload-hint">
                            Support JPG, PNG, SVG
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Status toggle */}
              <div className="form-group">
                <label className="toggle-container">
                  <span className="toggle-label">Active Status</span>
                  <div className="toggle-wrapper">
                    <input
                      type="checkbox"
                      checked={currentMethod.isActive}
                      onChange={handleToggleActive}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                  <span
                    className={`toggle-status ${
                      currentMethod.isActive ? "active" : "inactive"
                    }`}
                  >
                    {currentMethod.isActive ? "Enabled" : "Disabled"}
                  </span>
                </label>
              </div>

              {/* Action buttons */}
              <div className="editor-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setCurrentMethod(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="save-button"
                  onClick={handleSaveChanges}
                >
                  <Save size={16} />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty state when no method is selected */}
        {!currentMethod && (
          <div className="empty-editor">
            <div className="empty-illustration">
              <CreditCard size={64} />
            </div>
            <h3>Select a payment method</h3>
            <p>
              Click on any payment method from the list to update its details
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CollegeAccountUpdate;
